import { createFileRoute, Link } from '@tanstack/react-router'
import { useDeferredValue, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Building2,
  ChevronRight,
  FolderOpen,
  FolderPlus,
  Loader2,
  Plus,
  Search,
  User,
  X,
} from 'lucide-react'
import {
  useCreateProject,
  useProjects,
  useWorkspaces,
  type ProjectRead,
  type WorkspaceRead,
} from '@/api/queries'
import ErrorFallback from '@/components/ErrorFallback'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { formatError } from '@/lib/format-error'
import { canCreateProjects } from '@/lib/workspace-roles'

export const Route = createFileRoute('/_authenticated/projects/')({
  component: ProjectsIndexPage,
  errorComponent: ErrorFallback,
})

export function ProjectsIndexPage() {
  const { data: workspaces, isLoading, error } = useWorkspaces()

  // Sort workspaces with personal first, then alphabetical. Stable order
  // means the page doesn't reshuffle between refetches.
  const sorted = useMemo(() => {
    if (!workspaces) return []
    return [...workspaces].sort((a, b) => {
      if (a.is_personal !== b.is_personal) return a.is_personal ? -1 : 1
      return a.name.localeCompare(b.name)
    })
  }, [workspaces])

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 md:px-8">
      <PageHeader
        eyebrow="Workspace"
        title="Projects"
        description="Browse project context, folders, and project-scoped documents for each workspace."
      />

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading workspaces…
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          Failed to load workspaces: {formatError(error)}
        </p>
      )}

      {!isLoading && !error && sorted.length === 0 && (
        <EmptyState
          icon={Building2}
          title="No workspaces yet"
          description="You don't belong to any workspaces. Create one from the workspaces page to start grouping projects."
          action={
            <Button asChild size="sm">
              <Link to="/workspaces">Open workspaces</Link>
            </Button>
          }
        />
      )}

      {!isLoading && !error && sorted.length > 0 && (
        <div className="space-y-10">
          {sorted.map((ws) => (
            <WorkspaceProjectsSection key={ws.uuid} workspace={ws} />
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// One section per workspace.
// ---------------------------------------------------------------------------

function WorkspaceProjectsSection({ workspace }: { workspace: WorkspaceRead }) {
  const [searchInput, setSearchInput] = useState('')
  // ``useDeferredValue`` keeps the input itself snappy while letting React
  // schedule the keystroke that re-runs the query at a lower priority — a
  // lightweight stand-in for a debounced fetch without dragging in a hook.
  const deferredSearch = useDeferredValue(searchInput)
  const trimmedSearch = deferredSearch.trim()
  const activeQuery = trimmedSearch || null
  const isSearching = activeQuery !== null
  const { data: projects, isLoading, isFetching, error } = useProjects(
    workspace.uuid,
    activeQuery,
  )
  const canCreate = canCreateProjects(workspace.role)
  const searchInputId = `ws-${workspace.uuid}-project-search`
  // ``isLoading`` only fires the first time a given cache key resolves; for
  // an already-cached unfiltered slice, switching to a filtered key shows
  // ``isFetching`` instead. Track both so the skeleton state stays correct
  // when the user starts typing.
  const showLoadingSkeleton = isLoading || (isSearching && isFetching && !projects)

  return (
    <section className="space-y-3" aria-labelledby={`ws-${workspace.uuid}`}>
      <SectionHeader
        title={
          <span id={`ws-${workspace.uuid}`} className="flex items-center gap-2">
            {workspace.is_personal ? (
              <User className="size-4 text-muted-foreground" aria-hidden />
            ) : (
              <Building2 className="size-4 text-muted-foreground" aria-hidden />
            )}
            {workspace.name}
            {workspace.is_personal && (
              <Badge tone="outline" size="xs">
                Personal
              </Badge>
            )}
          </span>
        }
        description={
          isSearching
            ? projects
              ? `${projects.length} match${projects.length === 1 ? '' : 'es'} for “${trimmedSearch}”`
              : `Searching for “${trimmedSearch}”…`
            : projects && projects.length > 0
              ? `${projects.length} project${projects.length === 1 ? '' : 's'}`
              : 'No projects yet.'
        }
        actions={
          <>
            <div className="relative w-44 sm:w-56">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                id={searchInputId}
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search projects"
                aria-label={`Search projects in ${workspace.name}`}
                className="pl-8 pr-8"
                data-testid={`project-search-${workspace.uuid}`}
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label="Clear search"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
            {canCreate ? (
              <CreateProjectDialog workspaceUuid={workspace.uuid} />
            ) : (
              <Badge tone="outline" size="xs" title="Viewers can browse but not create.">
                {workspace.role}
              </Badge>
            )}
          </>
        }
      />

      {showLoadingSkeleton && (
        <div
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-busy="true"
          aria-label="Loading projects"
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-28" />
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {formatError(error)}
        </p>
      )}

      {!showLoadingSkeleton && !error && projects && projects.length === 0 && (
        isSearching ? (
          <EmptyState
            icon={Search}
            variant="bare"
            title={`No projects match “${trimmedSearch}”`}
            description="Try a different search term, or clear the filter to see every project."
            action={
              <Button variant="outline" size="sm" onClick={() => setSearchInput('')}>
                Clear search
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={FolderPlus}
            variant="bare"
            title="No projects in this workspace"
            description={
              canCreate
                ? 'Create your first project to start organising documents.'
                : 'Ask an editor or admin to create the first project for this workspace.'
            }
            action={canCreate ? <CreateProjectDialog workspaceUuid={workspace.uuid} /> : null}
          />
        )
      )}

      {!showLoadingSkeleton && !error && projects && projects.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="project-grid">
          {projects.map((project) => (
            <ProjectCard
              key={project.uuid}
              workspaceUuid={workspace.uuid}
              project={project}
            />
          ))}
        </div>
      )}
    </section>
  )
}

function ProjectCard({
  workspaceUuid,
  project,
}: {
  workspaceUuid: string
  project: ProjectRead
}) {
  return (
    <Link
      to="/projects/$workspaceUuid/$projectUuid"
      params={{ workspaceUuid, projectUuid: project.uuid }}
      className="group rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
      data-testid={`project-card-${project.uuid}`}
    >
      <Card interactive className="aurora-card-hover h-full">
        <CardHeader>
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
            >
              <FolderOpen className="size-4" />
            </span>
            <div className="min-w-0 space-y-1">
              <CardTitle className="truncate">{project.name}</CardTitle>
              <CardDescription>
                {project.document_count} doc
                {project.document_count !== 1 ? 's' : ''}
                {' · '}
                {project.folder_count} folder
                {project.folder_count !== 1 ? 's' : ''}
              </CardDescription>
            </div>
            <ChevronRight className="ml-auto mt-1.5 size-4 shrink-0 text-muted-foreground/70 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
          </div>
        </CardHeader>
        {project.description && (
          <CardContent>
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {project.description}
            </p>
          </CardContent>
        )}
      </Card>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Create-project dialog. Used both inline in the section header and inside
// the empty-state CTA — same component so behaviour stays consistent.
// ---------------------------------------------------------------------------

export function CreateProjectDialog({
  workspaceUuid,
  trigger,
}: {
  workspaceUuid: string
  trigger?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const createProject = useCreateProject(workspaceUuid)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await createProject.mutateAsync({
        name: trimmed,
        description: description.trim() || null,
      })
      toast.success(`Project "${trimmed}" created`)
      setName('')
      setDescription('')
      setOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button size="sm">
            <Plus />
            New project
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Create project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`project-name-${workspaceUuid}`}>Name</Label>
            <Input
              id={`project-name-${workspaceUuid}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q1 forms"
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`project-description-${workspaceUuid}`}>
              Description{' '}
              <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id={`project-description-${workspaceUuid}`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Tax documents for Q1"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={createProject.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createProject.isPending || !name.trim()}>
              {createProject.isPending && <Loader2 className="animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
